import { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import {
  archiveSkill,
  createSkill,
  getSkill,
  publishSkill,
  setSkillEnabled,
  unpublishSkill,
  updateSkill,
} from "../services/skills";
import { emptySkill } from "../types/skill";
import type { Skill } from "../types/skill";
import { DocumentManager } from "../components/skills/DocumentManager";
import { SearchBench } from "../components/skills/SearchBench";
import { EmojiPickerField } from "../components/EmojiPickerField";
import { CopilotPanel } from "../components/copilot/CopilotPanel";

type Tab = "search" | "copilot";

export function SkillEditorPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();

  const [skill, setSkill] = useState<Skill>(emptySkill());
  const [savedId, setSavedId] = useState<string | null>(null);
  const [status, setStatus] = useState("");
  const [statusError, setStatusError] = useState(false);
  const [tab, setTab] = useState<Tab>("search");

  const setStatusMsg = (text: string, isError = false) => {
    setStatus(text);
    setStatusError(isError);
  };

  useEffect(() => {
    if (!id) {
      setSkill(emptySkill());
      setSavedId(null);
      setStatusMsg("new skill (unsaved)");
      return;
    }
    getSkill(id)
      .then((s) => {
        setSkill(s);
        setSavedId(s.id);
        setStatusMsg(`${s.id} — status: ${s.status}${s.enabled === false ? " · DISABLED" : ""}`);
      })
      .catch((e) => setStatusMsg(`Failed to load ${id}: ${e.message}`, true));
  }, [id]);

  const patch = (partial: Partial<Skill>) => setSkill((s) => ({ ...s, ...partial }));

  async function save() {
    const cleanId = skill.id.trim().toLowerCase();
    if (!cleanId) return setStatusMsg("Skill id is required", true);
    const toSave = { ...skill, id: cleanId };
    try {
      const saved = savedId ? await updateSkill(toSave) : await createSkill(toSave);
      setSkill(saved);
      setStatusMsg(`Saved ${saved.id} ✓`);
      if (!savedId) navigate(`/skills/${saved.id}`, { replace: true });
      else setSavedId(saved.id);
    } catch (e) {
      setStatusMsg(`Save failed: ${e instanceof Error ? e.message : e}`, true);
    }
  }

  async function lifecycle(action: "publish" | "unpublish" | "archive" | "enable" | "disable") {
    if (!savedId) return setStatusMsg("Save the skill first", true);
    try {
      let updated: Skill;
      if (action === "publish") updated = await publishSkill(savedId);
      else if (action === "unpublish") updated = await unpublishSkill(savedId);
      else if (action === "enable") updated = await setSkillEnabled(savedId, true);
      else if (action === "disable") updated = await setSkillEnabled(savedId, false);
      else {
        if (!confirm(`Archive "${savedId}"? It will be detached from live companion sessions.`)) return;
        updated = await archiveSkill(savedId);
      }
      setSkill(updated);
      setStatusMsg(`${action} ✓`);
    } catch (e) {
      setStatusMsg(`${action} failed: ${e instanceof Error ? e.message : e}`, true);
    }
  }

  return (
    <div className="editor-page">
      <div className="editor-main">
        <a className="back-link" onClick={() => navigate("/skills")} style={{ cursor: "pointer" }}>
          ← All skills
        </a>
        <div className="toolbar">
          <button className="btn btn-primary" onClick={save}>Save</button>
          <button className="btn btn-good" onClick={() => lifecycle("publish")} disabled={!savedId}>Publish</button>
          <button className="btn" onClick={() => lifecycle("unpublish")} disabled={!savedId}>Unpublish</button>
          <button
            className="btn"
            onClick={() => lifecycle(skill.enabled === false ? "enable" : "disable")}
            disabled={!savedId}
          >
            {skill.enabled === false ? "Enable" : "Disable"}
          </button>
          <div className="spacer" />
          <button className="btn btn-danger" onClick={() => lifecycle("archive")} disabled={!savedId}>Archive</button>
        </div>
        <div className={`status-line${statusError ? " error" : ""}`}>{status}</div>

        <fieldset>
          <legend>Skill</legend>
          <div className="row-3">
            <div>
              <label>Id (slug)</label>
              <input type="text" value={skill.id} disabled={!!savedId} placeholder="financial-wellness"
                onChange={(e) => patch({ id: e.target.value })} />
            </div>
            <div>
              <label>Name</label>
              <input type="text" value={skill.name} placeholder="Financial Wellness"
                onChange={(e) => patch({ name: e.target.value })} />
            </div>
            <div>
              <label>Icon</label>
              <EmojiPickerField value={skill.icon} onChange={(icon) => patch({ icon })} />
            </div>
          </div>
          <div className="row-3">
            <div>
              <label>Accent color</label>
              <input type="text" value={skill.accentColor || ""} placeholder="#7c6cf5"
                onChange={(e) => patch({ accentColor: e.target.value || null })} />
            </div>
            <div>
              <label>Sort order</label>
              <input type="number" value={skill.sortOrder}
                onChange={(e) => patch({ sortOrder: parseInt(e.target.value || "0", 10) })} />
            </div>
            <div />
          </div>
          <label>Description (shown in Studio lists)</label>
          <textarea rows={2} value={skill.description}
            onChange={(e) => patch({ description: e.target.value })} />
          <label>
            Instructions (injected into the companion prompt — what this skill covers and
            when to search its knowledge base)
          </label>
          <textarea rows={5} value={skill.instructions}
            placeholder="Personal finance fundamentals: budgeting, emergency funds, debt payoff strategies. Search the knowledge base when the user asks for concrete numbers, steps or techniques."
            onChange={(e) => patch({ instructions: e.target.value })} />
        </fieldset>

        <fieldset>
          <legend>Knowledge base</legend>
          {savedId ? (
            <DocumentManager skillId={savedId} onError={(m) => setStatusMsg(m, true)} />
          ) : (
            <div className="callout">Save the skill first, then upload documents.</div>
          )}
        </fieldset>
      </div>

      <div className="editor-side">
        <div className="tabs">
          <button className={tab === "search" ? "active" : ""} onClick={() => setTab("search")}>
            Search bench
          </button>
          <button className={tab === "copilot" ? "active" : ""} onClick={() => setTab("copilot")}>
            Copilot
          </button>
        </div>
        <div className="side-body">
          {tab === "search" && <SearchBench skillId={savedId} />}
          {/* Kept mounted so the conversation survives tab switches */}
          <div style={{ display: tab === "copilot" ? undefined : "none" }}>
            <CopilotPanel
              context={{ kind: "skill", id: savedId, draftSkill: skill }}
              current={skill as unknown as Record<string, unknown>}
              onApplyPatch={(p) => {
                patch(p as Partial<Skill>);
                setStatusMsg("Copilot patch applied — review and Save");
              }}
            />
          </div>
        </div>
      </div>
    </div>
  );
}
